import React from 'react';
import PropTypes from 'prop-types';
import pick from 'object.pick';
import {FormattedMessage} from 'react-intl';

import {connect} from '@stubhub/react-store-provider';

import {query} from '../../../../modules/react-query/index';
import {
  electronicTicketMatchIds,
  mobileTicketMatchIds,
  mobileTransferMatchIds,
  paperTicketMatchIds,
} from '../../../../utils/constants';

import controller from './controller';
import {getArrayIntersectionByKeys, getSelectedIdsByKey, INSTANT_DELIVERY_TYPES} from './deliveryType-ids';
import MAP_TYPE, {MAP_TYPE_BY_INDEX} from './map_type';
import ListingsFilter from './view';

// Noinspection ParameterNamingConventionJS
function withListingFilterData(InnerComp) {
  @query({
    quantity: 'qty',
    priceWithFees: 'priceWithFees',
    listingCategories: 'categ',
    excludeCategories: 'excl',
    deliveryType: 'tdt',
    deliveryTypeInv: 'dt', // TODO: once moving all inventory to use the latest we can use the names for this interface
    sort: 'sort',
    sortKey: 'sortKey',
    sliderMin: 'sliderMin',
    sliderMax: 'sliderMax',
    priceBucket: 'priceBucket',
    mapType: 'mapType',
    multiSectionSelect: 'multiSectionSelect',
  })
  @connect(controller)
  class ListingFilterDataProvider extends React.PureComponent {
    static contextTypes = {
      track: PropTypes.object.isRequired,
    };

    UNSAFE_componentWillMount() {
      const {fetchFullfilmentWindow, eventId} = this.props;
      fetchFullfilmentWindow(eventId);
    }

    get numberOfTickets() {
      const {displayedQuantities = [], updateQuery, quantity: quantityFromURL} = this.props;

      const position = displayedQuantities.find((qty) => qty === quantityFromURL);

      return {
        position,
        displayedQuantities,
        onFilterQuantitySelect: (event, selectedFilterQuantity) => {
          const newURLQuantity = quantityFromURL !== selectedFilterQuantity ? selectedFilterQuantity : null;

          updateQuery &&
            updateQuery({
              quantity: newURLQuantity,
            });
        },
      };
    }

    get seatMapOptions() {
      const {
        updateQuery,
        mapType,
        venueConfig: {map: {mapType: venueConfigMapType} = {}} = {},
        multiSectionSelect: isMultiSelectOn = false,
        updateMaptype,
      } = this.props;
      const configuredMapType =
        mapType || (venueConfigMapType && MAP_TYPE_BY_INDEX[venueConfigMapType]) || MAP_TYPE.SECTION;

      return {
        currentMapType: configuredMapType,
        isMultiSelectOn,
        toggleMultiSelect: () => {
          updateQuery &&
            updateQuery({
              multiSectionSelect: !isMultiSelectOn,
            });
        },
        changeMapType: (e, newMapType) => {
          updateMaptype(newMapType);

          updateQuery &&
            updateQuery({
              mapType: newMapType,
            });
        },
      };
    }

    get popularFilters() {
      const {priceWithFees, deliveryType, updateQuery, availableTicketTypes, isInstantAvailable} = this.props;

      const {aisleFeature, onChange} = this.seatFeatures;

      /** Instant Delivery*/
      const instantSelected = deliveryType && !!this.isTicketTypeSelected('instant', this.ticketTypesSelected);
      const instantTypeKeys = INSTANT_DELIVERY_TYPES.filter((key) => {
        return availableTicketTypes.indexOf(key) >= 0;
      });
      const availableWithinMinutesKeys = instantSelected ? false : instantTypeKeys;
      const showWithinMinutes = instantTypeKeys.length > 0;

      return [
        {
          id: 'price-with-fees',
          label: (
            <FormattedMessage id="PricePerTicket.priceWithFeeTxt" defaultMessage="Show prices with estimated fees" />
          ),
          selected: priceWithFees,
          onClick: (e) => {
            e.preventDefault();
            updateQuery && updateQuery({priceWithFees: !priceWithFees});
          },
        },
        showWithinMinutes && {
          id: 'instant-delivery',
          label: <FormattedMessage id="TicketTypeSection.availableWithinMinutes" defaultMessage="Instant delivery" />,
          selected: instantSelected,
          disabled: !isInstantAvailable,
          onClick: (e) => {
            e.preventDefault();
            updateQuery &&
              updateQuery(
                {
                  deliveryType: availableWithinMinutesKeys,
                  deliveryTypeInv:
                    availableWithinMinutesKeys && this.getTicketTypesSelectedIds(availableWithinMinutesKeys),
                },
                false
              );
          },
        },
        aisleFeature && {
          id: 'aisle-seats',
          label: <FormattedMessage id="SeatFeature.aisle" defaultMessage="Aisle" />,
          selected: aisleFeature.selected,
          onClick: (event) => {
            onChange({event, id: aisleFeature.id, selected: !aisleFeature.selected, type: aisleFeature.type});
          },
        },
      ].filter((validFilters) => validFilters);
    }

    get pricingFilter() {
      return {
        ...pick(this.props, [
          'averageTicketPrice',
          'averageTicketPriceCurrency',
          'currency',
          'eventId',
          'eventPricingSummary',
          'maxPrice',
          'minPrice',
          'priceBucket',
          'priceWithFees',
          'sliderMax',
          'sliderMin',
          'updateQuery',
          'locale',
          'isLoading',
        ]),
        onChange: this.handlePricingFilterChange,
      };
    }

    // TODO: Maybe this should be the responsibility of PricingFilter Component?
    handlePricingFilterChange = (event, data) => {
      const {value, bucketMin, bucketMax, minSliderPosition, maxSliderPosition} = data;
      const {updateQuery} = this.props;
      updateQuery &&
        updateQuery({
          priceBucket: value,
          // TODO: If productionizing, don't use sliderMin/sliderMax, make new query params for price buckets
          sliderMin: `${bucketMin},${minSliderPosition}`,
          sliderMax: `${bucketMax},${maxSliderPosition}`,
        });
    };

    static getFilterOptionList(filterOptions) {
      return Array.isArray(filterOptions) ? filterOptions : [filterOptions];
    }

    get sortBy() {
      const {sortKey, showTicketRecoInd = true} = this.props;

      const onSortBySelection = ({key, value, selected}) => {
        const {updateQuery} = this.props;
        updateQuery &&
          updateQuery(
            {
              sort: selected && value,
              sortKey: selected && key,
            },
            true
          );
      };

      const sortByLabels = {
        lowestPrice: <FormattedMessage id="ListingsFilter.SortBy.Status.lowestPrice" defaultMessage="Lowest Price" />,
        bestValue: <FormattedMessage id="ListingsFilter.SortBy.Status.bestValue" defaultMessage="Best Value" />,
        bestSeats: <FormattedMessage id="ListingsFilter.SortBy.Status.bestSeats" defaultMessage="Best Seats" />,
        row: <FormattedMessage id="ListingsFilter.SortBy.Status.row" defaultMessage="Row" />,
      };

      if (showTicketRecoInd) {
        return {
          features: [
            {
              key: 'lowestPrice',
              selected: !sortKey || sortKey === 'lowestPrice',
              label: sortByLabels.lowestPrice,
              value: 'price asc,value desc',
            },
            {
              key: 'bestValue',
              selected: sortKey && sortKey === 'bestValue',
              label: sortByLabels.bestValue,
              value: 'value desc,price asc',
            },
            {
              key: 'bestSeats',
              selected: sortKey && sortKey === 'bestSeats',
              label: sortByLabels.bestSeats,
              value: 'quality desc,price asc',
            },
            {
              key: 'row',
              selected: sortKey && sortKey === 'row',
              label: sortByLabels.row,
              value: 'row asc',
            },
          ],
          onChange: onSortBySelection,
        };
      }

      return null;
    }

    isTicketTypeSelected = (label, selectedLabels = []) => {
      let result;
      if (Array.isArray(selectedLabels)) {
        result = selectedLabels.filter((item) => item === label).length > 0;
      } else {
        result = selectedLabels;
      }

      return result;
    };

    ticketTypeLabelMap = {
      paper: <FormattedMessage id="ListingsFilter.ticketType.physical" defaultMessage="Physical" />,
      electronic: <FormattedMessage id="ListingsFilter.ticketType.printAtHome" defaultMessage="Print-at-Home" />,
      mobile: <FormattedMessage id="ListingsFilter.ticketType.mobile" defaultMessage="Mobile" />,
      mobileTransfer: (
        <FormattedMessage id="ListingsFilter.ticketType.mobileTransfer" defaultMessage="Mobile Transfer" />
      ),
      instant: <FormattedMessage id="ListingsFilter.ticketType.instant" defaultMessage="Available within seconds" />,
    };

    get ticketTypesSelected() {
      const {deliveryType} = this.props;
      let deliveryTypeArray;
      if (Array.isArray(deliveryType)) {
        deliveryTypeArray = deliveryType;
      } else if (typeof deliveryType === 'string') {
        deliveryTypeArray = [deliveryType]; // Handle single selection case
      } else {
        deliveryTypeArray = []; // Handle invalid input case
      }

      return deliveryTypeArray;
    }

    getTicketTypesSelectedKeys = (obj) => {
      const {deliveryType} = this.props;
      let deliveryTypeArray;
      let result = [obj.key];

      if (deliveryType) {
        deliveryTypeArray = this.ticketTypesSelected;

        result = deliveryTypeArray.filter((item) => item !== obj.key);

        if (deliveryTypeArray.length === result.length) {
          result.push(obj.key);
        }

        if (result.length === 1 && result[0] === 'instant') {
          result = [];
        }
      }

      return result;
    };

    getTicketTypesSelectedIds(keys) {
      let selectedIds = false;
      const instantSelected = this.isTicketTypeSelected('instant', keys);
      if (instantSelected) {
        selectedIds = getArrayIntersectionByKeys(keys);
      } else {
        selectedIds = getSelectedIdsByKey(keys);
      }

      return selectedIds;
    }

    get ticketTypeStatus() {
      const ticketTypeLabels = [];
      const {ticketTypesSelected} = this;
      const ticketTypeLength = ticketTypesSelected.length;
      const lastSeparatorPosition = ticketTypeLength - 1;

      for (let i = 0; i < ticketTypeLength; i++) {
        ticketTypeLabels.push(this.ticketTypeLabelMap[ticketTypesSelected[i]]);
        if (i < lastSeparatorPosition) {
          ticketTypeLabels.push(', ');
        }
      }

      return ticketTypeLabels;
    }

    get ticketTypeButtonState() {
      const {availableTicketTypes} = this.props;
      const labelsSelected = this.ticketTypesSelected;
      const instantSelected = this.isTicketTypeSelected('instant', labelsSelected);

      const featureSets = [
        {
          key: 'paper',
          selected: this.isTicketTypeSelected('paper', labelsSelected),
          ids: paperTicketMatchIds,
          label: this.ticketTypeLabelMap.paper,
          disabled: instantSelected,
        },
        {
          key: 'electronic',
          selected: this.isTicketTypeSelected('electronic', labelsSelected),
          ids: electronicTicketMatchIds,
          label: this.ticketTypeLabelMap.electronic,
          disabled: false,
        },
        {
          key: 'mobile',
          selected: this.isTicketTypeSelected('mobile', labelsSelected),
          ids: mobileTicketMatchIds,
          label: this.ticketTypeLabelMap.mobile,
          disabled: false,
        },
        {
          key: 'mobileTransfer',
          selected: this.isTicketTypeSelected('mobileTransfer', labelsSelected),
          ids: mobileTransferMatchIds,
          label: this.ticketTypeLabelMap.mobileTransfer,
          disabled: instantSelected,
        },
      ];

      return featureSets.filter(({key}) => {
        return availableTicketTypes.indexOf(key) >= 0;
      });
    }

    get ticketType() {
      return {
        status: this.ticketTypeStatus,
        features: this.ticketTypeButtonState,
        onChange: (e, obj) => {
          const keys = this.getTicketTypesSelectedKeys(obj);
          const {updateQuery} = this.props;

          e.preventDefault();
          updateQuery &&
            updateQuery(
              {
                deliveryType: keys,
                deliveryTypeInv: this.getTicketTypesSelectedIds(keys),
              },
              false
            );
        },
      };
    }

    get listingCategories() {
      const {listingCategories = []} = this.props;
      let result;
      if (typeof listingCategories === 'number') {
        result = [String(listingCategories)];
      } else if (typeof listingCategories === 'string') {
        result = listingCategories.split(',');
      } else {
        result = listingCategories || [];
      }

      return result;
    }
    get excludeCategories() {
      const {excludeCategories = []} = this.props;

      if (typeof excludeCategories === 'number') {
        return [String(excludeCategories)];
      }

      if (typeof excludeCategories === 'string') {
        return excludeCategories.split(',');
      }

      return excludeCategories || [];
    }

    addIdToCategoryList(id, categoryList = []) {
      return [...categoryList, id];
    }

    removeFromCategoryList(id, categoryList) {
      return categoryList.filter((record) => record !== id);
    }

    get seatFeatures() {
      const {seatTraits = []} = this.props;
      const {listingCategories, excludeCategories} = this;

      const features = seatTraits.map(({displayName, name, id, type}) => {
        return {
          id,
          name,
          key: `sf${id}`,
          label: displayName,
          selected: listingCategories.indexOf(String(id)) >= 0 || excludeCategories.indexOf(String(id)) >= 0,
          type,
        };
      });

      const aisleFeature = features.find((trait = {}) => trait.name === 'aisle');

      return {
        features,
        aisleFeature,
        onChange: ({event, selected, id, type}) => {
          event.preventDefault();
          const {updateQuery} = this.props;
          let listingCategoriesTmp = [...listingCategories];
          let excludeCategoriesTmp = [...excludeCategories];
          const idStr = String(id);

          if (type === 'exclude') {
            if (selected) {
              excludeCategoriesTmp = this.addIdToCategoryList(idStr, excludeCategoriesTmp);
            } else {
              excludeCategoriesTmp = this.removeFromCategoryList(idStr, excludeCategoriesTmp);
            }
          } else if (type === 'include') {
            if (selected) {
              listingCategoriesTmp = this.addIdToCategoryList(idStr, listingCategoriesTmp);
            } else {
              listingCategoriesTmp = this.removeFromCategoryList(idStr, listingCategoriesTmp);
            }
          }

          updateQuery &&
            updateQuery(
              {
                listingCategories: listingCategoriesTmp,
                excludeCategories: excludeCategoriesTmp,
              },
              false
            );
        },
      };
    }

    // Decide if or not we show seat features section
    get hideSeatFeatures() {
      const {features = []} = this.seatFeatures;

      return features.length === 0;
    }

    get totalTickets() {
      const {totalTickets, totalListings} = this.props;

      return {
        totalTickets,
        totalListings,
      };
    }

    render() {
      const {
        onResetFilter,
        onCloseFilter,
        goBack,
        isGA,
        showFilterHeader,
        showFilterFooter,
        showSlideUpResetButton,
        device,
        locale,
        isPriceIncludeFeesPreselected,
        showPricingFilter,
      } = this.props;

      return (
        <InnerComp
          numberOfTickets={this.numberOfTickets}
          seatMapOptions={this.seatMapOptions}
          sortBy={this.sortBy}
          pricingFilter={this.pricingFilter}
          seatFeatures={this.seatFeatures}
          ticketType={this.ticketType}
          totalTickets={this.totalTickets}
          popularFilters={this.popularFilters}
          onCloseFilter={onCloseFilter}
          onResetFilter={onResetFilter}
          isGA={isGA}
          goBack={goBack}
          showFilterHeader={showFilterHeader}
          showFilterFooter={showFilterFooter}
          hideSeatFeatures={this.hideSeatFeatures}
          showSlideUpResetButton={showSlideUpResetButton}
          device={device}
          locale={locale}
          isPriceIncludeFeesPreselected={isPriceIncludeFeesPreselected}
          showPricingFilter={showPricingFilter}
        />
      );
    }
  }

  ListingFilterDataProvider.propTypes = {
    /**
     * Method to reset filters
     */
    onResetFilter: PropTypes.func,

    /**
     * Method to close filters
     */
    onCloseFilter: PropTypes.func,

    /**
     * Method to go back
     */
    goBack: PropTypes.func,

    /**
     * Is general admission?
     */
    isGA: PropTypes.bool,

    /**
     * If filter in header is shown
     */
    showFilterHeader: PropTypes.bool,

    /**
     * If filter in footer is shown
     */
    showFilterFooter: PropTypes.bool,

    /**
     * If reset button is available
     */
    showSlideUpResetButton: PropTypes.bool,

    /**
     * Object with device information
     */
    device: PropTypes.object,

    /**
     * Locale
     */
    locale: PropTypes.string,

    /**
     * If price include fees by default
     */
    isPriceIncludeFeesPreselected: PropTypes.bool,

    /**
     * If pricing filter is shown
     */
    showPricingFilter: PropTypes.bool,

    /**
     *
     */
    updateQuery: PropTypes.func,

    /**
     * Total of tickets available
     */
    totalTickets: PropTypes.number,

    /**
     * Total of listings
     */
    totalListings: PropTypes.number,

    /**
     *
     */
    seatTraits: PropTypes.array,

    /**
     *
     */
    excludeCategories: PropTypes.array,

    /**
     *
     */
    listingCategories: PropTypes.array,

    /**
     * Array of ticket types available
     */
    availableTicketTypes: PropTypes.array,

    /**
     * Listing delivery type
     */
    deliveryType: PropTypes.string,

    /**
     * Sorting key
     */
    sortKey: PropTypes.string,

    /**
     * If ticket reco is shown
     */
    showTicketRecoInd: PropTypes.bool,

    /**
     * Method to get fullfilment window
     */
    fetchFullfilmentWindow: PropTypes.func,

    /**
     * Event Id
     */
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /**
     * If price is with fees
     */
    priceWithFees: PropTypes.bool,

    /**
     * If instant delivery is available
     */
    isInstantAvailable: PropTypes.bool,

    /**
     * Type of map
     */
    mapType: PropTypes.string,

    /**
     * Venue of listing
     */
    venueConfig: PropTypes.object,

    /**
     * If selection of multiples sections is available
     */
    multiSectionSelect: PropTypes.bool,

    /**
     * Method to change map type
     */
    updateMaptype: PropTypes.func,

    /**
     * Number of tickets available for listing
     */
    displayedQuantities: PropTypes.array,

    /**
     * Number of tickets selected
     */
    quantity: PropTypes.number,
  };

  return ListingFilterDataProvider;
}

export default withListingFilterData(ListingsFilter);
